import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  AppBar,
  Box,
  Chip,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Badge,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ScienceIcon from "@mui/icons-material/Science";
import BusinessIcon from "@mui/icons-material/Business";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SyncIcon from "@mui/icons-material/Sync";
import ContactsIcon from "@mui/icons-material/Contacts";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import { useClerk } from "@clerk/clerk-react";
import { useAuth } from "../contexts/AuthContext";
import { getDashboardSummary } from "../services/api";
import { trackEvent } from "../services/analytics";

const DRAWER_WIDTH = 260;

const navItems = [
  { label: "Dashboard", path: "/", icon: <DashboardIcon /> },
  { label: "Pipeline Search", path: "/pipeline", icon: <ScienceIcon /> },
  { label: "Contact Search", path: "/contacts", icon: <ContactsIcon /> },
  { label: "Companies", path: "/companies", icon: <BusinessIcon /> },
  { label: "Alerts", path: "/alerts", icon: <NotificationsIcon /> },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { signOut } = useClerk();

  useEffect(() => {
    getDashboardSummary()
      .then((data) => setAlertCount(data.unread_alerts))
      .catch(() => {});
  }, []);

  // Track page views
  useEffect(() => {
    trackEvent("$pageview", { path: location.pathname });
  }, [location.pathname]);

  const allNavItems = isAdmin
    ? [...navItems, { label: "Data Sync", path: "/sync", icon: <SyncIcon /> }, { label: "Admin", path: "/admin", icon: <AdminPanelSettingsIcon /> }]
    : navItems;

  const remaining = user ? user.token_limit - user.tokens_used : 0;

  const drawer = (
    <Box>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src="/assets/logo-square.png"
          alt="Pharma Pipeline"
          sx={{ width: 180, height: 180 }}
        />
      </Box>
      <Divider />
      <List>
        {allNavItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
              trackEvent("nav_click", { destination: item.path, label: item.label });
            }}
            sx={{
              mx: 1,
              borderRadius: 2,
              mb: 0.5,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "& .MuiListItemIcon-root": { color: "white" },
                "&:hover": { backgroundColor: "primary.dark" },
              },
            }}
          >
            <ListItemIcon>
              {item.label === "Alerts" ? (
                <Badge badgeContent={alertCount} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Pharma Pipeline Intelligence
          </Typography>
          {user && (
            <Chip
              label={`${remaining} / ${user.token_limit} credits`}
              size="small"
              variant="outlined"
              color={remaining < 10 ? "error" : remaining < 25 ? "warning" : "default"}
              sx={{ mr: 1 }}
            />
          )}
          {user && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {user.email}
            </Typography>
          )}
          <IconButton onClick={() => { trackEvent("sign_out"); signOut(); }} title="Sign out" size="small">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, borderRight: "1px solid #eee" },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: 8,
          minHeight: "100vh",
          backgroundColor: "background.default",
        }}
      >
        <Outlet />
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", textAlign: "center", mt: 4, pb: 2 }}
        >
          Build: {process.env.REACT_APP_BUILD_TIME || "dev"}
        </Typography>
      </Box>
    </Box>
  );
}
